import { useEffect, useRef, useState, useCallback } from "react";

const FORMATS = [
  { id: "square", label: "Square feed · 1080×1080", w: 1080, h: 1080 },
  { id: "portrait", label: "Portrait feed · 1080×1350", w: 1080, h: 1350 },
  { id: "story", label: "Story / Reel · 1080×1920", w: 1080, h: 1920 },
  { id: "landscape", label: "Landscape / X · 1200×630", w: 1200, h: 630 },
];

const BACKDROPS = [
  { id: "ink", label: "Ink (solid black)", src: null },
  { id: "dome", label: "The Dome", src: "/artworks/the-dome.jpg" },
  { id: "light", label: "Light Language", src: "/artworks/light-language.jpg" },
  { id: "gaze", label: "Universal Gaze", src: "/artworks/universal-gaze.jpg" },
  { id: "blue", label: "Through the Blue", src: "/artworks/through-the-blue.jpg" },
  { id: "sign", label: "Sign From Above", src: "/artworks/sign-from-above.jpg" },
  { id: "tribal", label: "Tribal Goddess", src: "/release/tribal.jpg" },
  { id: "projection", label: "Chapter 2 projection", src: "/hero/projection.jpg" },
  { id: "portrait", label: "Nick — archive portrait", src: "/hero/nick-portrait.jpg" },
];

const imageCache = {};
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (imageCache[src]) return resolve(imageCache[src]);
    const img = new Image();
    img.onload = () => {
      imageCache[src] = img;
      resolve(img);
    };
    img.onerror = reject;
    img.src = src;
  });

const drawSpaced = (ctx, text, x, y, spacing, align = "center") => {
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total =
    widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, text.length - 1);
  let cx = align === "center" ? x - total / 2 : x;
  [...text].forEach((ch, i) => {
    ctx.fillText(ch, cx, y);
    cx += widths[i] + spacing;
  });
};

export const CampaignTemplate = () => {
  const canvasRef = useRef(null);
  const [format, setFormat] = useState(FORMATS[1]);
  const [backdrop, setBackdrop] = useState(BACKDROPS[0]);
  const [darkness, setDarkness] = useState(0.6);
  const [eyebrow, setEyebrow] = useState("ONE DAY ONLY");
  const [headline, setHeadline] = useState("CHAPTER 2\nBEGINS.");
  const [subline, setSubline] = useState(
    "Shoreditch Arts Club, London — 31 July 2026"
  );
  const [footer, setFooter] = useState("BOOK TICKETS — NICKFRASERARTIST.COM");

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { w, h } = format;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    await Promise.all([
      document.fonts.load('400 100px "Six Caps"'),
      document.fonts.load('italic 300 48px "Cormorant Garamond"'),
      document.fonts.load('300 30px "Manrope"'),
    ]);

    ctx.fillStyle = "#121212";
    ctx.fillRect(0, 0, w, h);

    if (backdrop.src) {
      try {
        const img = await loadImage(backdrop.src);
        const scale = Math.max(w / img.width, h / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
        ctx.fillStyle = `rgba(0,0,0,${darkness})`;
        ctx.fillRect(0, 0, w, h);
      } catch {
        // keep solid ink if the image fails
      }
    }

    // subtle grain
    ctx.fillStyle = "rgba(247,245,240,0.03)";
    for (let i = 0; i < 3500; i++) {
      ctx.fillRect(Math.random() * w, Math.random() * h, 1.2, 1.2);
    }

    const bone = "#f7f5f0";
    const cx = w / 2;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const headLines = headline
      .split("\n")
      .map((l) => l.trim().toUpperCase())
      .filter(Boolean);
    let headSize = Math.min(w * 0.17, (h * 0.5) / Math.max(1, headLines.length));
    ctx.font = `400 ${headSize}px "Six Caps"`;
    const maxLineW = Math.max(
      1,
      ...headLines.map((l) => ctx.measureText(l).width)
    );
    if (maxLineW > w * 0.86) headSize *= (w * 0.86) / maxLineW;
    const lineH = headSize * 1.02;

    const eyebrowSize = Math.max(18, w * 0.02);
    const subSize = Math.max(24, w * 0.036);
    const footSize = Math.max(15, w * 0.017);
    const blockH =
      (eyebrow ? eyebrowSize + headSize * 0.5 : 0) +
      headLines.length * lineH +
      (subline ? subSize * 2.2 : 0);
    let y = (h - blockH) / 2 + (format.id === "story" ? h * 0.04 : 0);

    if (eyebrow) {
      ctx.fillStyle = "rgba(247,245,240,0.75)";
      ctx.font = `300 ${eyebrowSize}px "Manrope"`;
      const ruleW = w * 0.05;
      ctx.fillRect(cx - ruleW / 2, y - eyebrowSize * 1.9, ruleW, 1);
      drawSpaced(ctx, eyebrow.toUpperCase(), cx, y, eyebrowSize * 0.42);
      y += headSize * 0.62;
    }

    ctx.fillStyle = bone;
    ctx.font = `400 ${headSize}px "Six Caps"`;
    headLines.forEach((l) => {
      y += lineH;
      ctx.fillText(l, cx, y - lineH * 0.18);
    });

    if (subline) {
      y += subSize * 1.7;
      ctx.fillStyle = "rgba(247,245,240,0.85)";
      ctx.font = `italic 300 ${subSize}px "Cormorant Garamond"`;
      ctx.fillText(subline, cx, y);
    }

    if (footer) {
      ctx.fillStyle = "rgba(247,245,240,0.65)";
      ctx.font = `300 ${footSize}px "Manrope"`;
      drawSpaced(ctx, footer.toUpperCase(), cx, h - h * 0.055, footSize * 0.38);
    }
  }, [format, backdrop, darkness, eyebrow, headline, subline, footer]);

  useEffect(() => {
    draw();
  }, [draw]);

  const download = () => {
    canvasRef.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nick-fraser-chapter2-${format.id}-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const field =
    "invisible-input !text-left w-full bg-transparent";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5 space-y-8">
        <div>
          <p className="hairline opacity-50 mb-3">Format</p>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFormat(f)}
                data-testid={`template-format-${f.id}`}
                className={`hairline border px-4 py-2 transition-colors duration-300 ${
                  format.id === f.id
                    ? "bg-[color:var(--ink)] text-[color:var(--bone)] border-[color:var(--ink)]"
                    : "border-[color:var(--rule)] hover:border-[color:var(--ink)]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="hairline opacity-50 mb-3">Background</p>
          <div className="flex flex-wrap gap-2">
            {BACKDROPS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBackdrop(b)}
                data-testid={`template-backdrop-${b.id}`}
                className={`hairline border px-4 py-2 transition-colors duration-300 ${
                  backdrop.id === b.id
                    ? "bg-[color:var(--ink)] text-[color:var(--bone)] border-[color:var(--ink)]"
                    : "border-[color:var(--rule)] hover:border-[color:var(--ink)]"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {backdrop.src && (
          <div>
            <p className="hairline opacity-50 mb-3">
              Darkness · {Math.round(darkness * 100)}%
            </p>
            <input
              type="range"
              min="0.25"
              max="0.85"
              step="0.05"
              value={darkness}
              onChange={(e) => setDarkness(Number(e.target.value))}
              className="w-full accent-[color:var(--ink)]"
              data-testid="template-darkness-slider"
            />
          </div>
        )}

        <div>
          <p className="hairline opacity-50 mb-2">Eyebrow line</p>
          <input
            value={eyebrow}
            onChange={(e) => setEyebrow(e.target.value)}
            className={field}
            data-testid="template-eyebrow-input"
          />
        </div>
        <div>
          <p className="hairline opacity-50 mb-2">
            Headline · new line = new row
          </p>
          <textarea
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            rows={2}
            className="w-full bg-transparent border-b border-[color:var(--rule)] focus:border-[color:var(--ink)] outline-none font-mono text-sm py-3 tracking-wider resize-none"
            data-testid="template-headline-input"
          />
        </div>
        <div>
          <p className="hairline opacity-50 mb-2">Subline (italic serif)</p>
          <input
            value={subline}
            onChange={(e) => setSubline(e.target.value)}
            className={field}
            data-testid="template-subline-input"
          />
        </div>
        <div>
          <p className="hairline opacity-50 mb-2">Footer line</p>
          <input
            value={footer}
            onChange={(e) => setFooter(e.target.value)}
            className={field}
            data-testid="template-footer-input"
          />
        </div>

        <button
          type="button"
          onClick={download}
          className="hairline border border-[color:var(--ink)] px-8 py-4 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
          data-testid="template-download-button"
        >
          Download PNG →
        </button>
      </div>

      <div className="lg:col-span-7">
        <p className="hairline opacity-50 mb-4">Live preview</p>
        <div className="bg-[color:var(--bone-deep)] border border-[color:var(--rule)] p-6 md:p-10 flex items-center justify-center">
          <canvas
            ref={canvasRef}
            className="max-w-full h-auto max-h-[70vh] shadow-2xl"
            data-testid="template-canvas"
          />
        </div>
      </div>
    </div>
  );
};
